import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { playBloomChime } from '../lib/bloomChime';

/**
 * BloomingFlowerOverlay — 3-second premium full-screen transition that
 * plays inside the current app *before* opening the destination app
 * in a new tab. Visualises the Trust ecosystem's "aesthetic growth"
 * brand metaphor: 6 petals bloom outward from the destination icon.
 *
 * The cinematic 480-ms whoosh + chime is fired from the *parent's*
 * onClick handler (see SuiteSwitcher) so iOS Safari accepts it inside
 * the same user-gesture frame. As a fallback we also fire it from this
 * component's mount effect — no harm if it has already played because
 * `playBloomChime` reuses a single shared AudioContext.
 *
 * Props:
 *   - target: { name, short, brandColor, icon, href }   (required)
 *   - onComplete: () => void                            (called after 3s)
 */
const PETALS = 6;

export default function BloomingFlowerOverlay({ target, onComplete }) {
  // Ensure onComplete still fires even if Framer's onAnimationComplete hiccups
  useEffect(() => {
    const t = setTimeout(() => onComplete?.(), 3000);
    // Fallback: if the parent didn't already fire the chime inside the
    // click handler (e.g. async SSO took >100ms), play it now. Idempotent.
    playBloomChime();
    return () => clearTimeout(t);
  }, [onComplete]);

  if (!target) return null;
  const brand = target.brandColor || '#4F46E5';
  if (typeof document === 'undefined') return null;

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[200] flex items-center justify-center
        bg-[radial-gradient(ellipse_at_center,rgba(15,23,42,0.96)_0%,rgba(2,6,23,0.99)_70%)]
        backdrop-blur-sm overflow-hidden"
      data-testid="blooming-flower-overlay"
      role="status"
      aria-live="polite"
    >
      {/* Bloom — 6 petals on a circle, each scales/fades in turn */}
      <div className="relative w-[300px] h-[300px] md:w-[420px] md:h-[420px]">
        {Array.from({ length: PETALS }).map((_, i) => {
          const angle = (360 / PETALS) * i;
          return (
            <motion.div
              key={i}
              className="absolute top-1/2 left-1/2 origin-bottom"
              style={{
                width: 70,
                height: 130,
                transformOrigin: '50% 100%',
                transform: `translate(-50%, -100%) rotate(${angle}deg)`,
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [0, 1.05, 1], opacity: [0, 0.85, 0.7] }}
              transition={{
                duration: 1.6,
                delay: 0.18 + i * 0.07,
                ease: [0.22, 1.18, 0.36, 1],
              }}
            >
              <div
                className="w-full h-full"
                style={{
                  borderRadius: '50% 50% 35% 35% / 60% 60% 40% 40%',
                  background: `radial-gradient(ellipse at 50% 80%, ${brand}aa 0%, ${brand}44 55%, transparent 100%)`,
                  filter: 'blur(0.5px)',
                  boxShadow: `0 0 40px ${brand}55`,
                }}
              />
            </motion.div>
          );
        })}

        {/* Soft halo behind the icon */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
            w-[110px] h-[110px] md:w-[160px] md:h-[160px] rounded-full blur-2xl pointer-events-none"
          style={{ background: brand, opacity: 0.45 }}
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.6 }}
          transition={{ duration: 1.4, delay: 0.4, ease: 'easeOut' }}
        />

        {/* Destination icon centre — white circular badge for high contrast */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
            flex items-center justify-center w-[88px] h-[88px] md:w-[120px] md:h-[120px]
            rounded-full bg-white shadow-[0_20px_60px_rgba(0,0,0,0.5)] ring-1 ring-slate-900/10"
          initial={{ scale: 0, rotate: -25 }}
          animate={{ scale: [0, 1.15, 1], rotate: 0 }}
          transition={{ duration: 1.2, delay: 0.55, ease: [0.22, 1.18, 0.36, 1] }}
        >
          {target.iconUrl ? (
            <img src={target.iconUrl} alt={target.name} className="w-12 h-12 md:w-16 md:h-16 object-contain" />
          ) : (
            <span className="text-2xl md:text-3xl font-extrabold tracking-tight" style={{ color: brand }}>
              {target.short}
            </span>
          )}
        </motion.div>
      </div>

      {/* Caption */}
      <motion.div
        className="absolute left-1/2 -translate-x-1/2 bottom-[18%] text-center px-6"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.4 }}
      >
        <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-indigo-300/80 mb-1">
          Trust Ecosystem
        </p>
        <h2 className="text-2xl md:text-3xl font-extrabold text-white">
          Opening {target.name}…
        </h2>
        <p className="mt-1.5 text-xs md:text-sm text-slate-400">{target.tagline}</p>
      </motion.div>
    </motion.div>,
    document.body
  );
}
